/**
 * @file DecisionView.tsx
 * @description
 * Provides the final IRB decision display (status = IRB_DECISION) with payment gating. 
 * If `submission.payment_completed` is false, we tell the user they cannot see the final decision 
 * until they complete payment. If payment is done, we fetch the `review_decisions` row from the DB 
 * for this submission and display the IRB's final decision details (APPROVED, COND_APPROVED, or DENIED), 
 * along with reviewer comments, timestamps, etc. We also present a placeholder 
 * "Download Decision Letter" button for future file retrieval or PDF generation.
 *
 * Key Features:
 * 1. Payment Gating: The final IRB decision is only shown if payment_completed == true. 
 * 2. Fetches the relevant `review_decisions` record using submission.id, displaying the `decision`, 
 *    `comments`, and so on.
 * 3. Provides a placeholder "Download Decision Letter" button if `decision` is APPROVED or COND_APPROVED.
 * 4. Displays fallback messages if no decision record is found or if decision is denied.
 *
 * @dependencies
 * - React, useEffect/useState for data fetching and local state management
 * - Supabase client from `lib/supabaseClient` for DB calls
 * - `SubmissionRow` interface from `SubmissionDetails` context or a local redefinition
 *
 * @notes
 * - This completes Step 6 in the Implementation Plan: IRB_DECISION view logic with payment gating.
 * - Future expansions could handle multiple decision cycles or versioning if needed.
 * - The `Download Decision Letter` currently logs to console. Replace with an actual doc retrieval 
 *   from storage in a future step (like docService if an official letter was uploaded).
 */

import React, { useEffect, useState } from 'react';
import { useSupabase } from '../../contexts/SupabaseContext';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../../hooks/useAuth';
import { SubmissionRow } from '../../types/submission';
import { useNavigate } from 'react-router-dom';

/**
 * Interface representing a single row in the `review_decisions` table.
 */
interface ReviewDecisionRow {
  id: string;
  submission_id: string;
  reviewer_id: string;
  decision: 'APPROVED' | 'COND_APPROVED' | 'DENIED';
  decision_data: string;
  created_at: string; // Timestamp of final decision
}

/**
 * SubmissionRow
 * -------------
 * Minimal shape of the submission for this component's usage.
 */

/**
 * DecisionViewProps
 * ---------------
 * Accepts a single `submission` object. We rely on submission.payment_completed 
 * to check payment gating, plus submission.id to fetch from `review_decisions`.
 */
interface DecisionViewProps {
  submission: SubmissionRow;
}

/**
 * Interface for document metadata from the documents table
 */
interface DocumentMetadata {
  id: string;
  submission_id: string;
  user_id: string | null;
  filename: string;
  file_path: string;
  uploaded_at: string;
  type: string;
  is_active?: boolean;
}

/**
 * DecisionView
 * ------------
 * Renders the final IRB decision details if payment is complete. Otherwise, displays a gating message.
 * 1) If payment_completed = false, show message that user must complete payment to see final decision.
 * 2) If payment_completed = true, fetch the row in `review_decisions` for submission.id and display:
 *     - decision (APPROVED, COND_APPROVED, or DENIED)
 *     - reviewer's rationale/comments
 *     - created_at
 *     - "Download Decision Letter" placeholder (for APPROVED or COND_APPROVED).
 */
const DecisionView: React.FC<DecisionViewProps> = ({ submission }) => {
  // Add these debug logs
  console.log('Submission:', submission);
  console.log('Payment completed:', submission.payment_completed);
  console.log('Payment completed type:', typeof submission.payment_completed);
  
  // State to hold the fetched review decision (if any) once payment is confirmed
  const [reviewDecision, setReviewDecision] = useState<ReviewDecisionRow | null>(null);
  // Loading indicator for the fetch operation
  const [loadingDecision, setLoadingDecision] = useState<boolean>(false);
  // If there's an error fetching or parsing
  const [decisionError, setDecisionError] = useState<string | null>(null);
  
  // New state for handling the decision letter document
  const [decisionLetter, setDecisionLetter] = useState<DocumentMetadata | null>(null);
  const [decisionLetterUrl, setDecisionLetterUrl] = useState<string | null>(null);
  const [loadingLetter, setLoadingLetter] = useState<boolean>(false);
  const [letterError, setLetterError] = useState<string | null>(null);
  
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Create a single authenticated Supabase client
  const supabase = useSupabase();

  const handleViewLockedSubmission = () => {
    // We'll pass the submissionId in the query so the read-only page can load it
    navigate(`/view-submission?submissionId=${submission.id}`);
  };

  /**
   * React effect to fetch the `review_decisions` row only if payment is completed.
   */
  useEffect(() => {
    if (!submission.payment_completed) {
      // If payment isn't complete, we skip fetching the final decision data
      return;
    }

    // Perform the fetch in an async function
    const fetchDecision = async () => {
      setLoadingDecision(true);
      setDecisionError(null);

      try {
        const { data, error } = await supabase
          .from('review_decisions')
          .select('*')
          .eq('submission_id', submission.id)
          .single();

        if (error) {
          console.error('Error fetching review decision:', error);
          setReviewDecision(null);
          setDecisionError('Error fetching final decision from the database.');
        } else if (!data) {
          setReviewDecision(null);
          setDecisionError('No final decision found for this submission.');
        } else {
          setReviewDecision(data as ReviewDecisionRow);
        }
      } catch (err: any) {
        console.error('Unexpected error while fetching decision:', err);
        setReviewDecision(null);
        setDecisionError('Unexpected error while fetching final decision.');
      } finally {
        setLoadingDecision(false);
      }
    };

    fetchDecision();
  }, [submission.id, submission.payment_completed, supabase]);

  /**
   * useEffect to fetch the decision letter document metadata
   */
  useEffect(() => {
    if (!submission.payment_completed || !reviewDecision || !user) {
      return;
    }

    const fetchDecisionLetter = async () => {
      setLoadingLetter(true);
      setLetterError(null);

      try {
        const { data, error } = await supabase
          .from('documents')
          .select('*')
          .eq('submission_id', submission.id)
          .eq('type', 'decision_letter')
          .eq('is_active', true)
          .single();

        if (error) {
          console.error('Error fetching decision letter document:', error);
          setDecisionLetter(null);
          setLetterError('Could not find the decision letter document.');
        } else if (!data) {
          setDecisionLetter(null);
          setLetterError('No decision letter document found for this submission.');
        } else {
          setDecisionLetter(data as DocumentMetadata);
        }
      } catch (err: any) {
        console.error('Unexpected error while fetching decision letter:', err);
        setDecisionLetter(null);
        setLetterError('Unexpected error while fetching the decision letter.');
      } finally {
        setLoadingLetter(false);
      }
    };

    fetchDecisionLetter();
  }, [submission.id, submission.payment_completed, reviewDecision, supabase, user]);

  /**
   * useEffect to generate a signed URL for the decision letter once we have the metadata
   */
  useEffect(() => {
    if (!decisionLetter || !user) return;
    
    const generateSignedUrl = async () => {
      setLoadingLetter(true);
      
      try {
        const { data, error } = await supabase.storage
          .from('tempus-created-docs')
          .createSignedUrl(decisionLetter.file_path, 3600); // 1 hour expiry
        
        if (error) {
          console.error('Error generating signed URL for decision letter:', error);
          setDecisionLetterUrl(null);
          setLetterError('Could not generate download link for the decision letter.');
          return;
        }
        
        setDecisionLetterUrl(data.signedUrl);
      } catch (err) {
        console.error('Unexpected error generating signed URL:', err);
        setDecisionLetterUrl(null);
        setLetterError('Unexpected error preparing the decision letter for download.');
      } finally {
        setLoadingLetter(false);
      }
    };
    
    generateSignedUrl();
  }, [decisionLetter, supabase, user]);

  /**
   * handleDownloadLetter
   * --------------------
   * Triggers the download of the decision letter using the signed URL
   * Forces download rather than preview in browser
   */
  const handleDownloadLetter = () => {
    if (!decisionLetterUrl || !decisionLetter) {
      setLetterError('Decision letter is not available for download.');
      return;
    }
    
    // Force download by converting to blob
    fetch(decisionLetterUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.blob();
      })
      .then(blob => {
        // Create blob URL and trigger download
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = decisionLetter.filename;
        document.body.appendChild(link);
        link.click();
        // Clean up
        window.URL.revokeObjectURL(blobUrl);
        document.body.removeChild(link);
      })
      .catch(err => {
        console.error("Download failed:", err);
        setLetterError("Download failed. Please try again.");
      });
  };

  /**
   * If payment is not completed, we do not display the final decision. 
   * Just a short message prompting them to visit the Billing page.
   */
  if (!submission.payment_completed) {
    return (
      <div>
        <p className="text-gray-700 mb-4">
          This submission has an IRB decision, but you must complete payment before you can view 
          the final decision details or download the decision letter.
        </p>
        <p className="text-gray-500">
          Please visit the <strong>Billing</strong> page to complete your payment. Once payment is 
          confirmed, come back here to see the final IRB decision.
        </p>
      </div>
    );
  }

  /**
   * If payment is completed but we're still loading the final decision from DB.
   */
  if (loadingDecision) {
    return (
      <div>
        <p className="text-gray-700">Loading the final decision details. Please wait...</p>
      </div>
    );
  }

  /**
   * If we tried to fetch the final decision but encountered an error or no record was found.
   */
  if (decisionError) {
    return (
      <div className="p-8">
        <h2 className="text-xl font-bold mb-4">IRB Decision Issued</h2>
        <p className="text-gray-700 mb-4">
          We were unable to retrieve a final decision record for this submission.
        </p>
        <p className="text-sm text-red-600 italic">{decisionError}</p>
      </div>
    );
  }

  /**
   * If payment is complete and we found a review decision row.
   */
  if (reviewDecision) {
    const { decision, created_at } = reviewDecision;
    const displayDate = new Date(created_at).toLocaleString();

    return (
      <div className="space-y-4">
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-4">
          <p className="text-gray-700 mb-2">
            <strong>Decision:</strong> {decision}
          </p>
          <p className="text-gray-700 mb-2">
            <strong>Decision Date:</strong> {displayDate}
          </p>
          </div>

        {decision === 'APPROVED' || decision === 'COND_APPROVED' ? (
          <div className="space-y-2">
            <p className="text-gray-700">
              Congratulations! Your study is {decision === 'APPROVED' ? 'fully approved' : 'conditionally approved'}.
            </p>
            <div className="prose max-w-none text-gray-700">
                <ReactMarkdown>{reviewDecision.decision_data}</ReactMarkdown>
            </div>
            
            <button
              onClick={handleDownloadLetter}
              disabled={loadingLetter || !decisionLetterUrl}
              className={`px-4 py-2 rounded ${
                loadingLetter || !decisionLetterUrl
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-500'
              } text-white`}
            >
              {loadingLetter ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Preparing Download...
                </span>
              ) : (
                "Download Decision Letter"
              )}
            </button>
            
            {letterError && (
              <p className="text-sm text-red-600 mt-2">
                {letterError}
              </p>
            )}
          </div>
        ) : (
          <div className="text-gray-700 bg-yellow-50 border-l-4 border-yellow-400 p-3">
            <p>
              The IRB decision is <strong>{decision}</strong>. You may address any feedback and 
              contact the IRB if you have questions or wish to reapply.
            </p>
          </div>
        )}
                {/* Locked Submission */}
                <div className="mb-6">
          <h3 className="font-semibold text-lg mb-2">View Submission (Read-Only)</h3>
          <p className="text-gray-700 mb-3">
            If you wish to review the submission details in a read-only format, click below:
          </p>
          <button
            onClick={handleViewLockedSubmission}
            className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700"
          >
            View Locked Submission
          </button>
          </div>
      </div>
      );
  }

  /**
   * If none of the above conditions match (e.g., no error, no loading, no decision found).
   * This is a fallback case and theoretically shouldn't happen if all logic is correct.
   */
  return (
    <div className="p-8">
      <h2 className="text-xl font-bold mb-4">IRB Decision Issued</h2>
      <p className="text-gray-700">
        We couldn't find a final review decision for this submission, even though payment is complete. 
        This is unusual. Please contact support if you believe this is an error.
      </p>
    </div>
  );
};

export default DecisionView;
